import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { ChartOptions, ChartType, ChartDataSets } from "chart.js";
import { Color } from "ng2-charts";
import * as pluginAnnotations from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";

import { BubbleChartDatasource } from "@/shared-module/models";
import { DialogService } from "@/shared-module/services";
import { ZoneComponent } from "@/shared-module/components";

@Component({
  selector: "app-bubblechart",
  templateUrl: "bubblechart.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class BubbleChartComponent implements ZoneComponent, OnInit {
  constructor(
    private viewContainerRef: ViewContainerRef,
    private dialogService: DialogService,
  ) {}

  @Input() data: BubbleChartDatasource;
  @Input() width: number;
  @Input() needpaddingLeft: boolean;
  @Input() isZoom: boolean;

  @ViewChild("template", { static: true }) template;
  public chartType: ChartType = "bubble";
  public chartLegend = true;
  public chartData: ChartDataSets[] = [];
  public chartColors: Color[] = [];
  public chartPlugins = [pluginAnnotations, ChartDataLabels];
  public chartOptions: ChartOptions & { annotation: any } = {
    responsive: true,
    plugins: {
      datalabels: {
        display: true,
        anchor(context) {
          const value = context.dataset.data[context.dataIndex];
          // tslint:disable-next-line:no-string-literal
          return value["r"] < 16 ? "end" : "center";
        },
        align(context) {
          const value = context.dataset.data[context.dataIndex];
          // tslint:disable-next-line:no-string-literal
          return value["r"] < 16 ? "end" : "center";
        },
        color(context) {
          const value = context.dataset.data[context.dataIndex];
          // tslint:disable-next-line:no-string-literal
          return value["r"] < 16 ? "#00001F" : "white";
        },
        font: {
          weight: "bold",
        },
        formatter(value, context) {
          // tslint:disable-next-line:no-string-literal
          return context.dataset.radius;
        },
        offset: 2,
        padding: 0,
      },
    },
    tooltips: {
      callbacks: {
        title: (items, data) => {
          return this.data.bubbles[items[0].datasetIndex].caption;
        },
        label: (tooltipItem, data) => {
          return this.data.bubbles[tooltipItem.datasetIndex].tooltip;
        },
      },
    },
    scales: {
      xAxes: [
        {
          id: "x-axis-0",
          offset: true,
          gridLines: {
            borderDash: [2, 5],
            drawBorder: false,
            display: true,
          },
          ticks: {
            callback: (value) => {
              return value + "%";
            },
          },
          scaleLabel: {
            display: true,
          },
        },
      ],
      yAxes: [
        {
          id: "y-axis-0",
          gridLines: {
            drawBorder: false,
            borderDash: [2, 5],
            display: true,
          },
          ticks: {
            callback: (value) => {
              return value + "%";
            },
          },
          scaleLabel: {
            display: true,
          },
        },
      ],
    },
    annotation: {
      annotations: [
        {
          type: "line",
          mode: "vertical",
          scaleID: "x-axis-0",
          value: "0",
          borderColor: "rgba(0, 0, 0, 0.25)",
          borderWidth: 1,
        },
      ],
    },
  };

  zoom() {
    this.dialogService.setZoomInfo(this.data.zoomInfo);
  }

  ngOnInit() {
    this.viewContainerRef.createEmbeddedView(this.template);

    if (this.data) {
      this.chartData = [];
      let minX = 0;
      let minY = 0;
      let maxX = 0;
      let maxY = 0;
      this.data.bubbles.forEach((b) => {
        if (b.x < minX) {
          minX = b.x;
        }
        if (b.y < minY) {
          minY = b.y;
        }
        if (b.x > maxX) {
          maxX = b.x;
        }
        if (b.y > maxY) {
          maxY = b.y;
        }

        const bubble: ChartDataSets = {
          data: [{ x: b.x, y: b.y, r: b.r }],
          radius: b.value,
          label: b.caption,
          backgroundColor: b.color,
          borderColor: b.color,
          hoverBackgroundColor: b.color,
          hoverBorderColor: b.color,
        };

        this.chartData.push(bubble);
      });

      this.chartOptions.scales.yAxes[0].scaleLabel.labelString =
        this.data.axisY.legend;
      this.chartOptions.scales.xAxes[0].scaleLabel.labelString =
        this.data.axisX.legend;
      this.chartOptions.aspectRatio = this.data.aspectRatio;

      if (minY < 0) {
        const mY = this.round(Math.max(Math.abs(minY), maxY) * 1.2);
        this.chartOptions.scales.yAxes[0].ticks.min = -mY;
        this.chartOptions.scales.yAxes[0].ticks.max = mY;
      } else {
        this.chartOptions.scales.yAxes[0].ticks.min = 0;
        this.chartOptions.scales.yAxes[0].ticks.max = this.round(maxY * 1.2);
      }

      if (minX < 0) {
        const mX = this.round(Math.max(Math.abs(minX), maxX) * 1.2);
        this.chartOptions.scales.xAxes[0].ticks.min = -mX;
        this.chartOptions.scales.xAxes[0].ticks.max = mX;
      } else {
        this.chartOptions.scales.xAxes[0].ticks.min = 0;
        this.chartOptions.scales.xAxes[0].ticks.max = this.round(maxX * 1.2);
      }
    }
  }

  round(nbr: number) {
    return Math.ceil(nbr / 10) * 10;
  }
}
